import React from 'react'
import get from 'lodash/get'

import { Page, StatusPill } from '@fielded/shared-ui'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'
import { getProgramReadableName } from '@fielded/fs-api/lib/program/tools'

import { getNavigationItem } from '../../../../../../common/utils/navigationInfo'
import { formatDate } from '../../../../../../van-shared/utils/utils'
import { batchIdToProductId, hasSkipValidationComment, isPsmResellerWarehouseUser } from '../../../../common/utils'
import { getPlannedQuantities } from '../../../../common/makeDeliveryItems'
import { useLocation } from 'react-router-dom'

import ShipmentHeaderOptionButton from '../../../../common/ShipmentHeaderOptionButton'
import LMDDeliveryDetailTransferInfo from './LMDDeliveryDetailTransferInfo'
import LMDDeliveryDetailVerificationInfo from './LMDDeliveryDetailVerificationInfo'
import LMDDeliveryDetailTable from './LMDDeliveryDetailTable'
import { logError } from '../../../../../../common/init-sentry'

const LMDDeliveryDetail = ({
  user,
  history,
  config,
  delivery,
  productsMap,
  appPrograms,
  sourceName
}) => {
  const isPsmResellerWarehouse = isPsmResellerWarehouseUser(config, user)
  const plannedQuantities = getPlannedQuantities(delivery)
  const isReceived = !!delivery.snapshotDates.received
  const isDeliveryPackedAlready = !!delivery.snapshotDates.packed

  const location = useLocation()

  const hasVerificationSnapshot = [SHIPMENT_STATUS.ARRIVED, SHIPMENT_STATUS.RECEIVED].includes(delivery.status)
  const displayVerificationFlags = !hasSkipValidationComment(delivery) && hasVerificationSnapshot
  const hasHeaderOptionButton = isPsmResellerWarehouse && [SHIPMENT_STATUS.NEW, SHIPMENT_STATUS.PACKED].includes(delivery.status)

  const lmdPath = '/shipments/last-mile-deliveries'
  const searchParams = get(location, 'state.fromSearch', '')

  const baseUrl = `${lmdPath}${searchParams}`

  const lowerCaseCountsMap = plannedQuantities
    ? Object.fromEntries(
      Object.entries(plannedQuantities.counts).map(([k, v]) => [k.toLowerCase(), v])
    ) : null

  const productList = Object.entries(delivery.counts || {}).map(([key, value]) => {
    const productId = batchIdToProductId(key).toLowerCase()
    const productFromMap = productsMap && productsMap.get ? productsMap.get(productId) : undefined
    const productMapName = get(productFromMap, 'fullName') || get(productFromMap, 'name')
    const productName = productMapName || value.name || value.productName || productId || key

    if (!productFromMap) {
      logError(new Error('Missing product in productsMap'), {
        context: 'LMDDeliveryDetail',
        shipmentNo: delivery && delivery.shipmentNo,
        productId,
        batchKey: key,
        destination: get(delivery, 'destination.name') || get(delivery, 'destination.sdp'),
        countsEntry: value
      })
    }

    const product = {
      ...value,
      id: productId,
      name: productName
    }

    if (lowerCaseCountsMap) {
      const packedProduct = lowerCaseCountsMap[key.toLowerCase()]
      product.packedQty = get(packedProduct, 'quantity', 0)
    }
    return product
  })

  return (
    <Page
      className='shipments'
    >
      <Page.ComposableHeader>
        {hasHeaderOptionButton && (
          <div className='shipment-header__meta'>
            <ShipmentHeaderOptionButton
              shipment={delivery}
              redirectLink={`${lmdPath}/list/details/${delivery.snapshotId}`}
              isEditable
            />
          </div>
        )}
        <Page.ComposableHeader.Breadcrumb
          items={[
            getNavigationItem({ item: 'shipments', config, includePath: false }),
            {
              title: 'Last mile deliveries',
              path: baseUrl
            },
            {
              title: delivery.shipmentNo,
              active: true
            }
          ]}
        />
        <Page.ComposableHeader.Content spread>
          <Page.ComposableHeader.Title title={`Delivery to ${delivery.destination.name || delivery.destination.sdp}`} />
        </Page.ComposableHeader.Content>
      </Page.ComposableHeader>
      <Page.Panel reduceBottomMargin>
        <Page.Panel.Section>
          <div className='vs-u-row vs-u-flex-wrap vs-u-gap-half'>
            <StatusPill
              label='Driver'
              value={delivery.driverName || 'No information'}
            />
            <StatusPill
              label='ID'
              value={delivery.shipmentNo}
            />
            <StatusPill
              label='Order creation date'
              value={formatDate(delivery.createdAt, 'long')}
            />
            <StatusPill
              label='Source'
              value={sourceName}
            />
            <StatusPill
              label='Program'
              value={getProgramReadableName(delivery.programId, appPrograms) || 'No information'}
            />
            <StatusPill
              label='Funder'
              value={delivery.funder || 'No information'}
            />
          </div>
        </Page.Panel.Section>
      </Page.Panel>
      <Page.Panel
        withBackground
        reduceBottomMargin
      >
        <Page.Panel.Section>
          <LMDDeliveryDetailTransferInfo delivery={delivery} />
        </Page.Panel.Section>
      </Page.Panel>
      {displayVerificationFlags && (
        <Page.Panel withBackground>
          <LMDDeliveryDetailVerificationInfo delivery={delivery} />
        </Page.Panel>
      )}
      <Page.Panel withMinimalMargin>
        <LMDDeliveryDetailTable
          productList={productList}
          isReceived={isReceived}
          isPacked={isDeliveryPackedAlready}
        />
      </Page.Panel>
    </Page>
  )
}

export default LMDDeliveryDetail
